import type { CarePracticeRequest, RequestStats, RequestTimelineEntry, RequestStatus } from "./care-practice-request";

let carePracticeRequestChannelSequence = 0;

interface CarePracticeRequestRealtimePayload {
    commit_timestamp?: string;
    eventType?: string;
    new?: Record<string, unknown> | null;
    old?: Record<string, unknown> | null;
    schema?: string;
    table?: string;
}

interface CarePracticeRequestRealtimeChannel {
    on(
        event: "postgres_changes",
        config: {
            event: "*";
            schema: "public";
            table: "care_practice_requests" | "cross_sector_data_sharing";
            filter: string;
        },
        callback: (payload: CarePracticeRequestRealtimePayload) => void,
    ): CarePracticeRequestRealtimeChannel;
    subscribe(callback?: (status: string, err?: Error) => void): unknown;
}

interface CarePracticeRequestRealtimeClient {
    channel(name: string): CarePracticeRequestRealtimeChannel;
    removeChannel(channel: unknown): void;
}

export function mapCarePracticeRequestRow(row: Record<string, unknown>): CarePracticeRequest {
    return {
        id: row.id as string,
        createdAt: row.created_at as string,
        updatedAt: row.updated_at as string,
        facilityId: (row.facility_id as string) ?? null,
        facilityName: row.facility_name as string | undefined,
        practiceId: (row.practice_id as string) ?? null,
        practiceName: row.practice_name as string | undefined,
        patientId: (row.patient_id as string) ?? null,
        patientName: row.patient_name as string | undefined,
        residentId: (row.resident_id as string) ?? null,
        residentName: row.resident_name as string | undefined,
        createdBy: row.created_by as string,
        createdByName: row.created_by_name as string | undefined,
        category: row.category as CarePracticeRequest["category"],
        priority: row.priority as CarePracticeRequest["priority"],
        status: row.status as CarePracticeRequest["status"],
        subject: row.subject as string,
        payload: (row.payload ?? {}) as CarePracticeRequest["payload"],
        lastComment: (row.last_comment as string) ?? null,
        lastCommentAt: (row.last_comment_at as string) ?? null,
        lastCommentBy: (row.last_comment_by as string) ?? null,
        direction: (row.direction as CarePracticeRequest["direction"]) ?? "CARE_TO_PRACTICE",
        resolvedAt: (row.resolved_at as string) ?? null,
        resolvedBy: (row.resolved_by as string) ?? null,
        resolutionNotes: (row.resolution_notes as string) ?? null,
    };
}

export function buildCarePracticeRequestStats(
    rows: ReadonlyArray<Record<string, unknown>> | null | undefined,
): RequestStats {
    const stats: RequestStats = { neu: 0, gesehen: 0, inBearbeitung: 0, rueckfrage: 0, total: 0 };

    for (const row of rows ?? []) {
        switch (row.status) {
            case "NEU":
                stats.neu++;
                break;
            case "GESEHEN":
                stats.gesehen++;
                break;
            case "IN_BEARBEITUNG":
                stats.inBearbeitung++;
                break;
            case "RUECKFRAGE":
                stats.rueckfrage++;
                break;
        }
        stats.total++;
    }

    return stats;
}

export function mapTimelineEntry(row: Record<string, unknown>): RequestTimelineEntry {
    return {
        id: String(row.id),
        createdAt: row.created_at as string,
        actorName: (row.actor_name as string) ?? "Unbekannt",
        command: row.command as string,
        fromStatus: (row.from_status as RequestStatus) ?? null,
        toStatus: (row.to_status as RequestStatus) ?? null,
        comment: (row.comment as string) ?? null,
        resolutionNotes: (row.resolution_notes as string) ?? null,
    };
}

export function createCarePracticeRequestSubscription(params: {
    client: CarePracticeRequestRealtimeClient;
    scope: "facility" | "practice";
    scopeId: string;
    onUpdate: () => void;
    onEvent?: (event: {
        channelName: string;
        filter: string;
        payload: CarePracticeRequestRealtimePayload;
        table: "care_practice_requests" | "cross_sector_data_sharing";
    }) => void;
    onStatus?: (event: {
        channelName: string;
        error: string | null;
        status: string;
    }) => void;
}): { unsubscribe: () => void } {
    const linkScopeColumns = params.scope === "facility"
        ? ["source_facility_id", "target_facility_id"]
        : ["source_practice_id", "target_practice_id"];
    const channelName = `care-requests:${params.scope}:${params.scopeId}:${++carePracticeRequestChannelSequence}`;
    const requestFilter = `${params.scope}_id=eq.${params.scopeId}`;

    let channel = params.client
        .channel(channelName)
        .on(
            "postgres_changes",
            {
                event: "*",
                schema: "public",
                table: "care_practice_requests",
                filter: requestFilter,
            },
            (payload) => {
                params.onEvent?.({
                    channelName,
                    filter: requestFilter,
                    payload,
                    table: "care_practice_requests",
                });
                params.onUpdate();
            },
        );

    for (const column of linkScopeColumns) {
        const filter = `${column}=eq.${params.scopeId}`;
        channel = channel.on(
            "postgres_changes",
            {
                event: "*",
                schema: "public",
                table: "cross_sector_data_sharing",
                filter,
            },
            (payload) => {
                params.onEvent?.({
                    channelName,
                    filter,
                    payload,
                    table: "cross_sector_data_sharing",
                });
                params.onUpdate();
            },
        );
    }

    const subscription = channel.subscribe((status, err) => {
        params.onStatus?.({
            channelName,
            error: err?.message ?? null,
            status,
        });
    });

    return {
        unsubscribe: () => {
            params.client.removeChannel(subscription);
        },
    };
}